<template>
  <v-container class="bg-white">
    <v-card
      class="pa-5"
      variant="text">
      <v-card-item>
        <v-card-title>Kalkulationsraum erstellen</v-card-title>
        <template #append>
          <v-fab
            elevation="0"
            icon="mdi-close"
            @click.prevent="$emit('close')"></v-fab>
        </template>
      </v-card-item>

      <v-card-text>
        <v-text-field
          v-model="name"
          label="Name" />
        <v-textarea
          v-model="description"
          label="Beschreibung" />
      </v-card-text>
    </v-card>
    <v-divider></v-divider>
    <v-card class="pa-5 ml-10 mr-10 mt-5">
      <v-card-title>Weitere Mitarbeiter</v-card-title>
      <v-card-text>
        <v-data-table
          class="border-table"
          density="compact"
          :headers="headers"
          hide-no-data
          item-value="id"
          :items="users">
          <template #item.actions="{ item }">
            <v-icon
              v-if="Number(item.id) !== Number(authStore.user.id)"
              color="grey"
              size="large"
              :data-cy="item.name + '-embedded-delete'"
              @click="deleteUserFromSpace(item.id)">
              mdi-delete-outline
            </v-icon>
          </template>
          <template #item.rights="{ item, value }">
            <v-select
              v-model="item.rights"
              :items="newMemberRoleOptions"></v-select>
          </template>
          <template #body.append>
            <tr>
              <td>
                <v-text-field
                  ref="newMemberEmailInput"
                  v-model="newMemberEmail"
                  :rules="[...emailRules, existsAlready]"
                  placeholder="Nutzer Email"></v-text-field>
              </td>
              <td>
                <v-select
                  v-model="newMemberRole"
                  :items="newMemberRoleOptions"></v-select>
              </td>
              <td>
                <v-btn
                  :disabled="!newMemberEmailInput?.isValid"
                  @click="addMember"
                  >Hinzufügen</v-btn
                >
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          elevation="3"
          @click="emits('close')">
          Abbrechen
        </v-btn>
        <v-btn
          elevation="3"
          @click="createSpace"
          >Erstellen</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script lang="ts" setup>
import { useSpaceStore } from "@/stores/spaceStore";
import axios from "axios";
import { useTranslation } from "i18next-vue";
import { onMounted, useTemplateRef } from "vue";
import { computed } from "vue";
import { ref } from "vue";
import { useConfirm, useSnackbar } from "vuetify-use-dialog";
import { SPACE_RIGHT_WRITE, spaceRightNames } from "@/utils/constants";
import { useMainStore } from "@/stores/mainStore";
import { useAuthStore } from "@/stores/authStore";
import { SPACE_RIGHT_READ } from "@/utils/constants";
import { useUserRules } from "@/validations/user.js";

const { t } = useTranslation();

const newMemberEmailInput = useTemplateRef("newMemberEmailInput");
const { emailRules } = useUserRules(t);
const existsAlready = (v: string) => {
  return (
    users.value.findIndex((user) => user.email === v) == -1 ||
    t("validations.exists-in-space")
  );
};

const createConfirm = useConfirm();
const createSnackbar = useSnackbar();

const emits = defineEmits(["close"]);

const name = ref("");
const description = ref("");

const newMemberEmail = ref("");
const newMemberRole = ref(SPACE_RIGHT_READ);

const newMemberRoleOptions = ref([
  { title: "Lesen", value: SPACE_RIGHT_READ },
  { title: "Schreiben", value: SPACE_RIGHT_WRITE }
]);

const users = ref([]);

const spaceStore = useSpaceStore();
const mainStore = useMainStore();
const authStore = useAuthStore();

const headers: any = computed(() => [
  {
    title: "Email",
    key: "email",
    sortable: false,
    width: "25%"
  },
  {
    title: "Rechte",
    key: "rights",
    sortable: false,
    value: (item) => spaceRightNames[item.rights],
    width: "25%"
  },
  {
    title: "",
    key: "actions",
    sortable: false,
    align: "end",
    width: "25%"
  }
]);

const writeUserCount = computed(() => {
  return users.value.filter((user) => user.rights & SPACE_RIGHT_WRITE).length;
});

function createSpace() {
  if (!name.value) {
    // createSnackbar(t("dialog.space.create.errors.name"), "error");
    return;
  }

  axios
    .post(`/api/calculation-spaces/`, {
      name: name.value,
      description: description.value
    })
    .then((result) => {
      console.log(result);
      const createUsers = [];
      for (const user of users.value) {
        createUsers.push(
          axios.post(`/api/calculation-spaces/${result.data.id}/add-user`, {
            email: user.email,
            rights: user.rights,
            roomName: name.value,
            inviterName:
              authStore.user.first_name + " " + authStore.user.last_name
          })
        );
      }

      Promise.all(createUsers)
        .then(() => {
          console.log("Users created successfully");
          mainStore.loadCalculationSpaces();
          emits("close");
        })
        .catch((error) => {
          console.error("Error creating users:", error);
        });
    })
    .catch((error) => console.error);
}

async function deleteUserFromSpace(id: string) {
  users.value = users.value.filter((user) => user.id !== id);
}

async function addMember() {
  if (!newMemberEmail || !newMemberRole) return;

  users.value.push({
    email: newMemberEmail.value,
    rights: newMemberRole.value
  });
  newMemberEmail.value = "";
  newMemberRole.value = SPACE_RIGHT_READ;
}
</script>
